<template>
  <div>
    <v-card>
      <v-card-title class="card-title-style d-flex align-center">
        <div>Pricing items</div>
        <div class="ml-auto d-flex flex-row">
          <v-tooltip v-if="!isChildProposal" bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="ml-1">
                <v-btn color="green" @click="addPricingPlansItems" icon>
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>
            </template>
            <span>Add pricing plans items</span>
          </v-tooltip>
        </div>
      </v-card-title>
      <v-card-text class="my-5 px-6">
        <div v-if="isDataLoaded" class="scrollable-table">
          <v-data-table
            :headers="headers"
            :items="items"
            hide-default-footer
            disable-sort
            :items-per-page="items.length"
          >
            <template v-slot:item.isFeature="{ item }">
              <v-icon v-if="item.isFeature" color="green">mdi-check-circle</v-icon>
              <v-icon v-else color="grey">mdi-close-circle</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="d-flex ga-2 justify-end">
                <v-btn :disabled="isChildProposal" class="color-green" @click="editPricingPlansItems(item)" icon>
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn :disabled="isChildProposal" class="color-red" @click="deleteAlert(item)" icon>
                  <v-icon>mdi-delete-empty-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
        <div v-else>Loading data...</div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">{{ isEditing ? 'Edit pricing item' : 'Add pricing item' }}</v-card-title>
        <v-form ref="form" v-model="formValid">
          <v-card-text class="d-flex flex-column">
            <div class="d-flex">
              <v-text-field class="mr-3" v-model="editedItem.name" label="Item name" :rules="[rules.required]"></v-text-field>
              <v-switch class="mt-3" v-model="editedItem.isFeature" label="Is it feature?" inset></v-switch>
            </div>
            <div v-for="([key]) in filteredItemKeys" :key="key">
              <v-text-field
                v-model="editedItem[key]"
                :label="planIdNameMap[key] || key"
                :rules="[rules.required]"
              ></v-text-field>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">
              Close
            </v-btn>
            <v-btn color="primary" @click="saveEditedPricingPlanItems">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import _ from "lodash";
import axiosInstance from "@/axios";

export default {
  data: () => ({
    isDataLoaded: false,
    dialog: false,
    isEditing: false,
    editedItem: {},
    formValid: true,
    rules: {
      required: v => v !== '' && v !== null && v !== undefined || 'Required field',
    },
  }),
  computed: {
    isChildProposal() {
      return this.$store.state.isChildProposal;
    },
    headers() {
      return [
        { text: "Item / Option", value: "name" },
        { text: "Is feature", value: "isFeature", align: "center" },
        ...this.$store.state.pricingPlans.map(plan => ({
          text: plan.name,
          value: `id-${plan.id}`
        })),
        { text: "Actions", value: "actions", align: "end" }
      ];
    },
    items() {
      return _(this.$store.state.pricingPlansItems)
        .groupBy('name')
        .map((group, name) => {
          const row = { name, isFeature: group[0].isFeature || false };
          group.forEach(item => {
            const key = `id-${item.packageId}`;
            row[key] = item.value;
            row[`${key}_id`] = item.id;
          });
          return row;
        })
        .value();
    },
    filteredItemKeys() {
      // eslint-disable-next-line no-unused-vars
      const { name, valueIds, isFeature, ...rest } = this.editedItem;
      return this.$store.state.pricingPlans.map(plan => {
        const key = `id-${plan.id}`;
        return [key, rest[key]];
      });
    },
    planIdNameMap() {
      return this.$store.state.pricingPlans.reduce((map, plan) => {
        map[`id-${plan.id}`] = plan.name;
        return map;
      }, {});
    },
    proposalId() {
      return Number(this.$route.params.id);
    },
  },
  async created() {
    await this.refreshData();
    this.isDataLoaded = true;
  },
  methods: {
    async addPricingPlansItems() {
      this.isEditing = false;
      this.editedItem = {
        name: '',
        isFeature: false,
        ...Object.fromEntries(
          this.$store.state.pricingPlans.flatMap(plan => [[`id-${plan.id}`, ''], [`id-${plan.id}_id`, 0]])
        )
      };
      this.dialog = true;
    },
    async editPricingPlansItems(item) {
      this.isEditing = true;
      this.editedItem = { ...item };
      this.dialog = true;
    },
    async saveEditedPricingPlanItems() {
      const isValid = await this.$refs.form.validate();
      if (!isValid) return;

      const body = Object.entries(this.editedItem)
        .filter(([key]) => key.startsWith('id-') && !key.endsWith('_id'))
        .map(([key, value]) => {
          const packageId = Number(key.replace('id-', ''));
          const id = this.editedItem[`${key}_id`] || 0;

          return {
            Id: id,
            Name: this.editedItem.name,
            PackageId: packageId,
            Value: value,
            IsFeature: this.editedItem.isFeature || false
          };
        });

      try {
        await axiosInstance.post(`/proposal/${this.proposalId}/package/packages/items`, body);
        this.dialog = false;
        await this.refreshData();
      } catch (err) {
        console.error(err);
      }
    },
    closeDialog() {
      this.dialog = false;
      this.editedItem = {};
    },
    deleteAlert(item) {
      this.$swal({
        title: "Do you want to delete these items?",
        showDenyButton: true,
        confirmButtonText: `Yes`,
        denyButtonText: `No`,
      }).then((result) => {
        if (result.isConfirmed) {
          const ids = Object.entries(item)
            .filter(([key]) => key.endsWith('_id'))
            // eslint-disable-next-line no-unused-vars
            .map(([_, id]) => id);
          this.deletePricingPlanItems(ids);
        }
      });
    },
    async deletePricingPlanItems(ids) {
      const idsString = ids.join('&ids=');
      try {
        await axiosInstance.delete(`/proposal/${this.proposalId}/package/packages/items?ids=${idsString}`);
        await this.refreshData();
      } catch (err) {
        console.log(err);
      }
    },
    async refreshData() {
      await this.$store.dispatch("refreshPricingPlans", this.proposalId);
      await this.$store.dispatch("refreshPricingPlansItems", this.proposalId);
    },
  },
};
</script>

<style scoped>
.scrollable-table {
  max-height: 50vh;
  overflow-y: auto;
}
</style>
